/**
 * Table import — turn any supported tabular file (Excel, CSV, TSV, etc.)
 * into a single CSV text string that the existing `/parse-csv` LLM
 * pipeline can normalize.
 *
 * SheetJS handles every spreadsheet variant we care about in a single
 * library that's already bundled.
 */
import * as XLSX from 'xlsx';

export interface TableReadResult {
  /** CSV text suitable for `csv_text:` payloads. Header row first. */
  csvText: string;
  /** Human-readable label of the source format (for UI messages). */
  sourceFormat: string;
  /** Sheet name selected (Excel only). null for single-sheet sources. */
  sheetName: string | null;
  /** Names of all sheets in the workbook (Excel only). */
  availableSheets: string[];
  /** Approximate row count, including header. */
  rowCount: number;
}

/** A single sheet within a workbook, pre-parsed to CSV text. */
export interface SheetCandidate {
  name: string;
  csvText: string;
  /** Data rows excluding the header. */
  dataRowCount: number;
}

export interface WorkbookResult {
  /** Human-readable label of the source format (for UI messages). */
  sourceFormat: string;
  /** Every sheet in the workbook, in document order. Always >= 1. */
  sheets: SheetCandidate[];
}

const EXCEL_EXTS = ['xlsx', 'xls', 'xlsm', 'xlsb', 'ods', 'fods'];
const DELIMITED_EXTS = ['csv', 'tsv', 'txt'];

export function getFileExtension(file: File): string {
  const idx = file.name.lastIndexOf('.');
  return idx >= 0 ? file.name.slice(idx + 1).toLowerCase() : '';
}

export function isSupportedTableFile(file: File): boolean {
  const ext = getFileExtension(file);
  return EXCEL_EXTS.includes(ext) || DELIMITED_EXTS.includes(ext);
}

/**
 * Read any supported table file and return its content as CSV text.
 * Excel workbooks: returns the first sheet by default, or `preferSheet`
 * if it exists.
 */
export async function readTableFileAsCsv(
  file: File,
  opts: { preferSheet?: string } = {}
): Promise<TableReadResult> {
  const ext = getFileExtension(file);

  if (DELIMITED_EXTS.includes(ext)) {
    // Plain delimited text. SheetJS will auto-detect tab/comma/semicolon.
    const text = await file.text();
    const workbook = XLSX.read(text, { type: 'string', raw: true });
    const sheetName = workbook.SheetNames[0];
    const csvText = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
    return {
      csvText: csvText.trim(),
      sourceFormat: ext.toUpperCase(),
      sheetName: null,
      availableSheets: [sheetName],
      rowCount: csvText.split('\n').filter(Boolean).length,
    };
  }

  if (EXCEL_EXTS.includes(ext)) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName =
      (opts.preferSheet && workbook.SheetNames.includes(opts.preferSheet))
        ? opts.preferSheet
        : workbook.SheetNames[0];
    if (!sheetName) {
      throw new Error('Workbook has no sheets.');
    }
    const csvText = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
    return {
      csvText: csvText.trim(),
      sourceFormat: ext.toUpperCase(),
      sheetName,
      availableSheets: workbook.SheetNames,
      rowCount: csvText.split('\n').filter(Boolean).length,
    };
  }

  throw new Error(
    `Unsupported file type: .${ext}. Supported: ${[...EXCEL_EXTS, ...DELIMITED_EXTS].join(', ')}`
  );
}

/**
 * Read any supported table file and return *every* sheet inside it as
 * pre-parsed CSV candidates. Use this when you want to offer the user a
 * sheet picker — delimited files (.csv/.tsv) always return a single
 * synthetic sheet so callers can treat both cases uniformly.
 */
export async function readWorkbookSheets(file: File): Promise<WorkbookResult> {
  const ext = getFileExtension(file);

  if (DELIMITED_EXTS.includes(ext)) {
    const text = await file.text();
    const workbook = XLSX.read(text, { type: 'string', raw: true });
    const sheetName = workbook.SheetNames[0];
    const csvText = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]).trim();
    return {
      sourceFormat: ext.toUpperCase(),
      sheets: [{
        name: sheetName || 'Sheet1',
        csvText,
        dataRowCount: Math.max(0, csvText.split('\n').filter(Boolean).length - 1),
      }],
    };
  }

  if (EXCEL_EXTS.includes(ext)) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheets: SheetCandidate[] = workbook.SheetNames.map((name) => {
      const csvText = XLSX.utils.sheet_to_csv(workbook.Sheets[name]).trim();
      return {
        name,
        csvText,
        dataRowCount: Math.max(0, csvText.split('\n').filter(Boolean).length - 1),
      };
    });
    return {
      sourceFormat: ext.toUpperCase(),
      sheets,
    };
  }

  throw new Error(
    `Unsupported file type: .${ext}. Supported: ${[...EXCEL_EXTS, ...DELIMITED_EXTS].join(', ')}`
  );
}

/**
 * `accept` attribute for an <input type="file"> that wants all supported
 * table formats.
 */
export const TABLE_FILE_ACCEPT =
  '.csv,.tsv,.txt,.xlsx,.xls,.xlsm,.xlsb,.ods';
